// ════════════════════════════════════════════════════════════════════════════
// Reading a persisted attach block back — stored text in, items out.
//
//   ``` no-edit-data-block
//   verified info for 18/08/2026 17:58 (Auto pasted), DO NOT MODIFY , …
//   kind: hotels
//   about: Hotels in Gion, Kyoto · 12 Oct–15 Oct, 2 adults
//
//   - TSUKIMI HOTEL | hotel | ⭐⭐ | 4.8/5 [506 reviews]
//     where: Gion, Kyoto, JP
//     link: https://cabal.smalltree.cloud/l/booking.com/gZGYR64d
//   ```
//
// The reading half of `render-sentinel.ts`, and the one the FE's attach
// component consumes. It follows `parse-map-block.ts` — the standing
// fenced-block precedent — in shape and in temperament: pure, capped,
// drop-counting, and tolerant of every way a block can arrive slightly
// different from how it was written.
//
// TOLERANT where a surface can reshape text without changing what it says:
// any bullet character, any indent run, blank lines anywhere, an unknown
// directive, a `kind` this build does not know. STRICT where reliability
// depends on it: a field line is `key: value` split at the first colon after
// the key, so a value keeps its own colons and bars; a repeated key collects
// IN ORDER, because that order is the sub-array it encodes.
//
// WHY THE CAPTURE TIME COMES BACK AS A LABEL. The block prints the capture
// instant as a READING of it (`DD/MM/YYYY HH:mm`, UTC — see
// `captureDatetimeLabel`), never as the instant itself, so what a reader can
// recover is that reading. `AttachBlockDocument` carries the instant and is
// the RENDER side's type; this parser answers its own type, whose one
// difference is the field that cannot round-trip.
// ════════════════════════════════════════════════════════════════════════════

import type { PillarCode } from '../session/orchestration/tool-protocol/follow-up-refs.js';
import {
  SENTINEL_CAPTION_PREFIX,
  SENTINEL_CAPTION_SUFFIX,
} from './attached-result-constants.js';
import { AttachedResultKind } from './enums.js';
import { fenceSpansOfKind, type FenceSpan } from './fence-spans.js';
import { attachPillarForName } from './pillar-names.js';
import {
  ATTACH_DIRECTIVE_REGEX,
  ATTACH_FENCE_INFO_STRING,
  ATTACH_FIELD_LINE_REGEX,
  ATTACH_ITEM_BULLET_REGEX,
  ATTACH_MAX_BLOCK_LENGTH,
  ATTACH_MAX_FIELDS_PER_ITEM,
  ATTACH_MAX_ITEMS,
  ATTACH_MAX_LINE_LENGTH,
  ATTACH_WRITER_LABEL_FIELD_KEY,
  AttachDirectiveKey,
  type AttachFieldLine,
  type AttachItemBlock,
} from './grammar.js';

/**
 * One stored block, read.
 *
 * `pillar` is the `kind:` directive's value, read back as the pillar it names
 * (ruled 2026-08-20: "kind: names the pillar"). It is null when the block names
 * something this build does not know — a newer deploy's pillar, a word a hand
 * typed, or a block written before the directive carried a pillar at all. That
 * is not an error condition: BL and FE deploy independently and a block outlives
 * both, so the ruled behaviour for an unrecognized name is the neutral render —
 * the items still read, and no card claims to be a pillar's. Never a guess.
 */
/** One parsed item: the grammar's item, plus the ONE common metadata property
 *  stage-one parsing owns. `writerLabel` is the reserved
 *  `ATTACH_WRITER_LABEL_FIELD_KEY` line's value, LIFTED off `fields` here —
 *  before any strict pillar reader sees the item — so a labelled item meets
 *  its pillar's exact field vocabulary and can never downgrade a block. */
export interface ParsedAttachItem extends AttachItemBlock {
  readonly writerLabel: string | null;
}

export interface ParsedAttachDocument {
  /** The capture datetime exactly as the caption states it, or null when the
   *  block carries no caption of ours. */
  readonly capturedAtLabel: string | null;
  readonly pillar: PillarCode | null;
  /** True when the `kind:` line names the plain-link family (decision 8) —
   *  a block with no search behind it, links pasted as links. `pillar` is
   *  null for these, but the reverse does not hold: an unrecognized name is
   *  also a null pillar, and only this flag says the block DECLARED itself
   *  plain rather than naming something this build does not know. */
  readonly plainLinks: boolean;
  readonly about: string | null;
  readonly items: readonly ParsedAttachItem[];
  /** Items past `ATTACH_MAX_ITEMS`, and items whose headline was over the line
   *  ceiling. */
  readonly droppedItemCount: number;
  /** Field lines past `ATTACH_MAX_FIELDS_PER_ITEM`, field lines over the line
   *  ceiling, and field lines that arrived before any item opened. */
  readonly droppedFieldCount: number;
  /** Column-0 `key: value` lines whose key is outside `AttachDirectiveKey`. */
  readonly unknownDirectiveCount: number;
}

/** One located block and what it holds — what the FE mounts a card from. */
export interface LocatedAttachDocument {
  readonly span: FenceSpan;
  readonly parsed: ParsedAttachDocument;
}

/** The parser's working state. Only `parseAttachDocument` mutates it, on its
 *  own local copy; every helper below is a pure function returning a value. */
interface ParseAccumulator {
  capturedAtLabel: string | null;
  pillar: PillarCode | null;
  plainLinks: boolean;
  about: string | null;
  items: { headline: string; fields: AttachFieldLine[] }[];
  droppedItemCount: number;
  droppedFieldCount: number;
  unknownDirectiveCount: number;
}

const DIRECTIVE_KEYS: ReadonlySet<string> = new Set(Object.values(AttachDirectiveKey));

/** The caption's datetime, or null. Our caption text is specific enough that a
 *  line carrying both halves of it is one — nobody writes that sentence by
 *  accident. */
function capturedAtLabelOf(input: { line: string }): string | null {
  const line = input.line.trim();
  if (!line.startsWith(SENTINEL_CAPTION_PREFIX)) return null;
  if (!line.endsWith(SENTINEL_CAPTION_SUFFIX)) return null;
  const label = line.slice(
    SENTINEL_CAPTION_PREFIX.length,
    line.length - SENTINEL_CAPTION_SUFFIX.length,
  );
  return label.length > 0 ? label : null;
}

function trimmedOrNull(input: { text: string }): string | null {
  const trimmed = input.text.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** An item line, whether or not the block arrived indented. The bullet is
 *  written at column 0; a surface that indented the WHOLE block moved every
 *  line together, and the bullet is what still says "item" after it did. */
function headlineOf(input: { rawLine: string }): string | null {
  const match = input.rawLine.match(ATTACH_ITEM_BULLET_REGEX)
    ?? input.rawLine.trim().match(ATTACH_ITEM_BULLET_REGEX);
  return match === null ? null : (match[1] ?? '').trim();
}

/** A field line: indented, `key: value`, split at the first colon AFTER a key
 *  the grammar accepts. The key charset cannot hold a colon, so a value keeps
 *  every colon, bar and separator it was captured with. */
function fieldOf(input: { rawLine: string }): AttachFieldLine | null {
  const match = input.rawLine.match(ATTACH_FIELD_LINE_REGEX);
  if (match === null) return null;
  const key = (match[1] ?? '').trim();
  return key.length === 0 ? null : { key, value: (match[2] ?? '').trim() };
}

/** A column-0 directive, applied. An unknown key is counted and dropped —
 *  never guessed at, and never read as an item. */
function readDirective(input: { target: ParseAccumulator; rawLine: string }): boolean {
  const match = input.rawLine.match(ATTACH_DIRECTIVE_REGEX);
  if (match === null) return false;
  const key = (match[1] ?? '').toLowerCase();
  const value = (match[2] ?? '').trim();
  if (!DIRECTIVE_KEYS.has(key)) {
    input.target.unknownDirectiveCount += 1;
    return true;
  }
  if (key === AttachDirectiveKey.Kind) {
    // The pillar the block names, through the one table that printed it — a
    // name outside it answers null rather than being carried as a string
    // nothing downstream could act on. The one non-pillar name is the
    // plain-link family's own (decision 8), recognised as the declared flag
    // rather than folded into "unknown".
    input.target.pillar = attachPillarForName({ name: value });
    input.target.plainLinks =
      value.trim().toLowerCase() === (AttachedResultKind.Links as string);
    return true;
  }
  input.target.about = trimmedOrNull({ text: value });
  return true;
}

/** A field under the item it belongs to. A field line with no item above it
 *  has nothing to describe, so it is counted and dropped rather than
 *  attached to whatever item comes next. */
function collectField(input: { target: ParseAccumulator; field: AttachFieldLine }): void {
  const item = input.target.items[input.target.items.length - 1];
  if (item === undefined || item.fields.length >= ATTACH_MAX_FIELDS_PER_ITEM) {
    input.target.droppedFieldCount += 1;
    return;
  }
  item.fields.push(input.field);
}

/**
 * One `no-edit-data-block` block body.
 *
 * Null when the block holds no item: an attach with nothing attached is not a
 * lenient parse of something, it is an empty block, and there is no card to
 * mount from one. The same rule `parseMapBlock` applies to a map with no pins.
 */
export function parseAttachDocument(raw: string): ParsedAttachDocument | null {
  // Defensive ceiling on the whole block — a pathological block is never even
  // split into lines past this point.
  const bounded =
    raw.length > ATTACH_MAX_BLOCK_LENGTH ? raw.slice(0, ATTACH_MAX_BLOCK_LENGTH) : raw;

  const target: ParseAccumulator = {
    capturedAtLabel: null,
    pillar: null,
    plainLinks: false,
    about: null,
    items: [],
    droppedItemCount: 0,
    droppedFieldCount: 0,
    unknownDirectiveCount: 0,
  };

  bounded.split('\n').forEach((line) => {
    // Defensive ceiling per line. Skipped outright rather than truncated, so a
    // half-read value can never become a fact nobody captured. A dropped line
    // is counted as whatever it was going to be.
    const rawLine = line.endsWith('\r') ? line.slice(0, -1) : line;
    if (rawLine.length > ATTACH_MAX_LINE_LENGTH) {
      countOverLongLine({ target, rawLine });
      return;
    }
    if (rawLine.trim().length === 0) return;

    const caption = capturedAtLabelOf({ line: rawLine });
    if (caption !== null) {
      target.capturedAtLabel = caption;
      return;
    }

    // Bullet first, then field, then directive. The three are disjoint by
    // construction — a bullet opens with `-`, `*` or `+`, a field line opens
    // with whitespace, a directive opens with a letter at column 0 — so the
    // order is readability, not precedence.
    const headline = headlineOf({ rawLine });
    if (headline !== null) {
      if (target.items.length >= ATTACH_MAX_ITEMS) {
        target.droppedItemCount += 1;
        return;
      }
      target.items.push({ headline, fields: [] });
      return;
    }
    const field = fieldOf({ rawLine });
    if (field !== null) {
      collectField({ target, field });
      return;
    }
    readDirective({ target, rawLine });
  });

  if (target.items.length === 0) return null;
  return {
    capturedAtLabel: target.capturedAtLabel,
    pillar: target.pillar,
    plainLinks: target.plainLinks,
    about: target.about,
    items: target.items.map((item) => liftedWriterLabelItemOf({ item })),
    droppedItemCount: target.droppedItemCount,
    droppedFieldCount: target.droppedFieldCount,
    unknownDirectiveCount: target.unknownDirectiveCount,
  };
}

/** The reserved writer-label key, lifted off the item's field list into the
 *  common `writerLabel` property — EVERY occurrence leaves `fields`, so the
 *  strict authored readers meet exactly the pillar's own vocabulary and a
 *  labelled item can never downgrade a block. The first value is the label
 *  (the read a field stated at most once gets everywhere else); a repeat has
 *  nothing further to say. Empty after the grammar's own trim is no label. */
function liftedWriterLabelItemOf(input: {
  item: { headline: string; fields: AttachFieldLine[] };
}): ParsedAttachItem {
  const label = input.item.fields.find(
    (field) => field.key === ATTACH_WRITER_LABEL_FIELD_KEY,
  )?.value;
  return {
    headline: input.item.headline,
    fields: input.item.fields.filter(
      (field) => field.key !== ATTACH_WRITER_LABEL_FIELD_KEY,
    ),
    writerLabel: label === undefined || label.length === 0 ? null : label,
  };
}

/** An over-long line is dropped, and the drop is attributed: a bullet was an
 *  item, an indented line was a field. Anything else was noise and is not
 *  counted as either. */
function countOverLongLine(input: { target: ParseAccumulator; rawLine: string }): void {
  if (headlineOf({ rawLine: input.rawLine }) !== null) {
    input.target.droppedItemCount += 1;
    return;
  }
  if (ATTACH_FIELD_LINE_REGEX.test(input.rawLine)) {
    input.target.droppedFieldCount += 1;
  }
}

/**
 * Every value one item holds under `key`, in the order the block prints them.
 *
 * The ORDER is the data: the grammar writes an ordered sub-array as a repeated
 * key (three `segment:` lines are a three-leg itinerary, in order), so a reader
 * that took only the first would silently drop the legs after it.
 *
 * Here, beside the parse, because a consumer filtering `item.fields` itself
 * would be a second reading of the grammar — the drift the one-parser rule
 * exists to forbid. Keys are matched as the block prints them, which is how
 * each pillar's own field enum spells them.
 */
export function attachFieldValues(input: {
  item: AttachItemBlock;
  key: string;
}): string[] {
  return input.item.fields
    .filter((field) => field.key === input.key)
    .map((field) => field.value);
}

/** The first value under `key`, or null when the item carries none — the read
 *  for a field a pillar states at most once. */
export function attachFieldValue(input: {
  item: AttachItemBlock;
  key: string;
}): string | null {
  return attachFieldValues(input)[0] ?? null;
}

/** Every persisted attach block in a document, located and read, in document
 *  order. Blocks holding no item are absent — there is nothing to mount from
 *  one. */
export function locateAttachDocuments(input: {
  markdown: string;
}): LocatedAttachDocument[] {
  return fenceSpansOfKind({ markdown: input.markdown, info: ATTACH_FENCE_INFO_STRING })
    .map((span) => {
      const parsed = parseAttachDocument(span.body);
      return parsed === null ? null : { span, parsed };
    })
    .filter((located): located is LocatedAttachDocument => located !== null);
}
